"use client";

import { AnimatePresence, motion } from "motion/react";
import { useEffect, useRef, useState } from "react";
import { Text } from "@/components/primitives";
import { useTranslation } from "@/core/i18n";
import { cn } from "@/core/utils";
import { useProcessingProgress, useSmoothedProgress } from "@/hooks";
import ChecklistStepsPanel from "./ChecklistStepsPanel";
import EducationPanel from "./EducationPanel";
import LoadableProcessingImage from "./LoadableProcessingImage";
import ProcessingImageFrame from "./ProcessingImageFrame";
import ProcessingOverlayLayer from "./ProcessingOverlayLayer";
import ProcessingProgressBar from "./ProcessingProgressBar";
import type { EducationalCard, ProcessingDisplayProps } from "./types";
const ANALYSIS_ESTIMATED_DURATION_MS = 25_000;
const ANALYSIS_COMPLETION_STEP_DELAY_MS = 700;
const ANALYSIS_COMPLETION_EXIT_DELAY_MS = 2_500;
const AFFIRMING_ROTATION_INTERVAL_MS = 1_200;
const DEFAULT_ANALYSIS_LATE_FINALIZING_PROGRESS = 99;
const DEFAULT_ANALYSIS_LATE_FINALIZING_PROGRESS_DURATION_MS = 800;
export default function ProcessingDisplay({
  image,
  steps,
  educationConfig,
  minDurationMs,
  estimatedDurationMs,
  completionStepDelayMs,
  completionExitDelayMs,
  isComplete = false,
  onAllStepsComplete,
  centeredOnScreen,
  presentation = "analysis",
  analysisLateFinalizing,
  skipToFinalizing = false,
  initialProgress
}: ProcessingDisplayProps): React.ReactElement {
  const analysisLateFinalizingTimeoutMs = analysisLateFinalizing?.timeoutMs;
  const analysisLateFinalizingLeadMs = analysisLateFinalizing?.leadMs;
  const analysisLateFinalizingSpinnerDelayMs = analysisLateFinalizing?.spinnerDelayMs;
  const analysisLateFinalizingProgress = analysisLateFinalizing?.progress ?? DEFAULT_ANALYSIS_LATE_FINALIZING_PROGRESS;
  const {
    t
  } = useTranslation();
  const [affirmingIndex, setAffirmingIndex] = useState(0);
  const processingStartedAtRef = useRef(Date.now());
  const lateFinalizingBaseProgressRef = useRef<number | null>(null);
  const [lateFinalizingElapsedMs, setLateFinalizingElapsedMs] = useState<number | null>(null);
  const iconDisplayTiming = {
    estimatedDurationMs: estimatedDurationMs ?? ANALYSIS_ESTIMATED_DURATION_MS,
    completionStepDelayMs: completionStepDelayMs ?? ANALYSIS_COMPLETION_STEP_DELAY_MS,
    completionExitDelayMs: completionExitDelayMs ?? ANALYSIS_COMPLETION_EXIT_DELAY_MS
  };
  const {
    progress,
    activeStepIndex,
    phase,
    isProcessing,
    handleExitComplete
  } = useProcessingProgress({
    stepCount: steps.items.length,
    isComplete,
    minProcessingMs: minDurationMs,
    onAllStepsComplete,
    estimatedDurationMs: steps.variant === "icon" ? iconDisplayTiming.estimatedDurationMs : estimatedDurationMs,
    completionStepDelayMs: steps.variant === "icon" ? iconDisplayTiming.completionStepDelayMs : completionStepDelayMs,
    completionExitDelayMs: steps.variant === "icon" ? iconDisplayTiming.completionExitDelayMs : completionExitDelayMs,
    initialProgress
  });
  const iconSteps = steps.variant === "icon" ? steps.items : [];
  const safeStepIndex = iconSteps.length === 0 ? 0 : Math.min(activeStepIndex, iconSteps.length - 1);
  const activeIconStep = iconSteps[safeStepIndex];
  const shouldCenter = centeredOnScreen ?? steps.variant === "icon";
  const educationalCards: EducationalCard[] = educationConfig?.educationalCards ?? [];
  const isAnalysisWrapping = presentation === "analysis" && (skipToFinalizing || phase === "completing" && progress < 100);
  const isAnalysisAffirming = !skipToFinalizing && presentation === "analysis" && phase === "completing" && progress >= 100;
  const lateFinalizingStartsAtMs = analysisLateFinalizingTimeoutMs !== undefined && analysisLateFinalizingLeadMs !== undefined ? Math.max(0, analysisLateFinalizingTimeoutMs - analysisLateFinalizingLeadMs) : null;
  useEffect(() => {
    if (presentation !== "analysis" || isComplete || lateFinalizingStartsAtMs === null) {
      setLateFinalizingElapsedMs(null);
      return;
    }
    const intervalId = window.setInterval(() => {
      const elapsed = Date.now() - processingStartedAtRef.current - lateFinalizingStartsAtMs;
      setLateFinalizingElapsedMs(elapsed);
    }, 100);
    setLateFinalizingElapsedMs(Date.now() - processingStartedAtRef.current - lateFinalizingStartsAtMs);
    return () => window.clearInterval(intervalId);
  }, [isComplete, lateFinalizingStartsAtMs, presentation]);
  const isAnalysisLateFinalizing = presentation === "analysis" && !isComplete && lateFinalizingElapsedMs !== null && lateFinalizingElapsedMs >= 0;
  const isAnalysisLateFinalizingSpinner = isAnalysisLateFinalizing && lateFinalizingElapsedMs !== null && analysisLateFinalizingSpinnerDelayMs !== undefined && lateFinalizingElapsedMs >= analysisLateFinalizingSpinnerDelayMs;
  useEffect(() => {
    if (isAnalysisLateFinalizing) {
      lateFinalizingBaseProgressRef.current ??= progress;
      return;
    }
    lateFinalizingBaseProgressRef.current = null;
  }, [isAnalysisLateFinalizing, progress]);
  const rawDisplayedProgress = isAnalysisLateFinalizing && !isAnalysisLateFinalizingSpinner ? (() => {
    const progressDurationMs = Math.min(analysisLateFinalizingSpinnerDelayMs ?? DEFAULT_ANALYSIS_LATE_FINALIZING_PROGRESS_DURATION_MS, DEFAULT_ANALYSIS_LATE_FINALIZING_PROGRESS_DURATION_MS);
    const baseProgress = lateFinalizingBaseProgressRef.current ?? progress;
    if (progressDurationMs <= 0 || lateFinalizingElapsedMs === null) {
      return Math.max(progress, analysisLateFinalizingProgress);
    }
    const completionRatio = Math.min(lateFinalizingElapsedMs / progressDurationMs, 1);
    const easedCompletion = 1 - Math.pow(1 - completionRatio, 3);
    const lateProgress = baseProgress + (analysisLateFinalizingProgress - baseProgress) * easedCompletion;
    return Math.max(progress, lateProgress);
  })() : progress;
  const displayedProgressHWM = useRef(0);
  if (rawDisplayedProgress > displayedProgressHWM.current) {
    displayedProgressHWM.current = rawDisplayedProgress;
  }
  const visibleProgress = useSmoothedProgress(displayedProgressHWM.current, isAnalysisLateFinalizing);
  const isLateFinalizingSpinnerReady = visibleProgress >= Math.max(97, analysisLateFinalizingProgress - 1);
  const showAnalysisLateFinalizingSpinner = skipToFinalizing || isAnalysisLateFinalizingSpinner && isLateFinalizingSpinnerReady;
  const affirmingMessages = presentation === "analysis" ? [t("assessmentSteps.processing.affirming.one"), t("assessmentSteps.processing.affirming.two"), t("assessmentSteps.processing.affirming.three")] : [];
  useEffect(() => {
    if (!isAnalysisAffirming || affirmingMessages.length <= 1) {
      return;
    }
    const intervalId_0 = window.setInterval(() => {
      setAffirmingIndex(currentIndex => (currentIndex + 1) % affirmingMessages.length);
    }, AFFIRMING_ROTATION_INTERVAL_MS);
    return () => window.clearInterval(intervalId_0);
  }, [affirmingMessages.length, isAnalysisAffirming]);
  const displayedAffirmingIndex = affirmingMessages.length > 0 ? affirmingIndex % affirmingMessages.length : 0;
  return <AnimatePresence mode="wait" onExitComplete={handleExitComplete}>
      {isProcessing && <motion.div className={cn("flex min-h-[calc(100vh-128px)] flex-col items-center px-5 py-8", shouldCenter && "justify-center")} exit={{
      opacity: 0,
      scale: 0.97
    }} key="processing-display" transition={{
      duration: 0.3,
      ease: "easeOut"
    }}>
          <div className={cn("w-full", steps.variant === "checklist" ? "flex max-w-sm flex-col items-center gap-5 sm:max-w-md" : "max-w-md text-center")}>
            {steps.variant === "checklist" ? <>
                <motion.div animate={{
            opacity: 1,
            scale: 1
          }} className="relative mx-auto w-fit" initial={{
            opacity: 0,
            scale: 0.9
          }}>
                  <ProcessingImageFrame alt={image.alt} src={image.src} variant={image.variant}>
                    <ProcessingOverlayLayer activeStepIndex={activeStepIndex} overlay={image.overlay} phase={phase} />
                  </ProcessingImageFrame>
                </motion.div>
                <ChecklistStepsPanel activeStepIndex={activeStepIndex} steps={steps.items} />
                <div className="w-full px-1">
                  <ProcessingProgressBar progress={progress} showPercent={true} />
                </div>
                <div className="w-full">
                  <EducationPanel educationalCards={educationalCards} />
                </div>
              </> : <div className="mx-auto flex w-full max-w-sm flex-col items-center text-center">
                <motion.div animate={{
            opacity: 1,
            scale: 1
          }} className="relative mb-8 sm:mb-10" initial={{
            opacity: 0,
            scale: 0.92
          }}>
                  <div className="relative h-48 w-48 overflow-hidden rounded-full shadow-lg sm:h-52 sm:w-52">
                    <LoadableProcessingImage alt={image.alt} imageClassName="absolute inset-0 h-full w-full object-cover" skeletonClassName="h-full w-full rounded-[inherit]" src={image.src} />
                    <div aria-hidden className="pointer-events-none absolute inset-0" style={{
                background: phase === "completing" ? "radial-gradient(circle at 50% 40%, hsl(var(--primary) / 0.12) 0%, transparent 70%)" : "transparent"
              }} />
                    <ProcessingOverlayLayer activeStepIndex={activeStepIndex} overlay={image.overlay} phase={phase} />
                  </div>
                </motion.div>

                <AnimatePresence mode="wait">
                  <motion.div animate={{
              opacity: 1,
              y: 0
            }} className="mb-6 min-h-[74px] w-full max-w-sm" exit={{
              opacity: 0,
              y: -8
            }} initial={{
              opacity: 0,
              y: 8
            }} key={isAnalysisAffirming ? `affirming-${displayedAffirmingIndex}` : isAnalysisWrapping ? "analysis-wrapping" : `${presentation}-${activeIconStep.id}`} transition={{
              duration: 0.28,
              ease: "easeOut"
            }}>
                    {isAnalysisAffirming ? <div className="space-y-1.5">
                        <Text align="center" component="h2" text={affirmingMessages[displayedAffirmingIndex] ?? ""} variant="heading-md" weight="medium" />
                        <Text align="center" component="p" text={t("assessmentSteps.processing.affirmingSubtitle")} tone="muted" variant="body-sm" />
                      </div> : isAnalysisWrapping ? <div className="space-y-1.5">
                        <Text align="center" component="h2" text={t("assessmentSteps.processing.wrappingTitle")} variant="heading-md" weight="medium" />
                        <Text align="center" component="p" text={t("assessmentSteps.processing.wrappingSubtitle")} tone="muted" variant="body-sm" />
                      </div> : <div className="space-y-1.5">
                        <Text align="center" component="p" text={t(activeIconStep.titleKey)} variant="heading-md" weight="medium" />
                        <Text align="center" component="p" text={t(activeIconStep.bodyKey)} tone="muted" variant="body-sm" />
                      </div>}
                  </motion.div>
                </AnimatePresence>

                <div className="w-full max-w-[18rem]">
                  {showAnalysisLateFinalizingSpinner ? <div aria-atomic="true" className="flex flex-col items-center" role="status">
                      <div aria-hidden="true" className="border-primary/25 border-t-primary h-8 w-8 animate-spin rounded-full border-[3px]" data-testid="analysis-finalizing-spinner" />
                      <div className="sr-only">
                        {t("assessmentSteps.processing.wrappingSubtitle")}
                      </div>
                    </div> : <ProcessingProgressBar progress={visibleProgress} showPercent={true} variant="analysis" />}
                </div>
              </div>}
          </div>
        </motion.div>}
    </AnimatePresence>;
}
export type { AnalysisLateFinalizingConfig, EducationalCard, ProcessingDisplayProps } from "./types";